import type { FC } from "react"
import React, { useState, useEffect, useRef } from "react"
import { useTranslation, Trans } from "react-i18next"
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material"
import { UButton } from "@/components/index"
import { useAppDispatch } from "@/core/app/hooks"
import {openRefinement} from "@/core/app/slices/clinical/refinement/refinementThunkApi"
import { viewControlInTreatment } from "@/gluelayer"

/**
 *
 * @param treatUrl : Open the case's routing , for example:`/clinical/patient/{patientId}/case/{caseId}/treat?from=patientList`
 * @param open : Boolean value controlling pop-up display
 * @param setOpen : Boolean value controlling pop-up display
 */
interface StartRefinementProps {
  treatUrl?: string
  open: boolean
  setOpen: (arg: boolean) => void
}
const StartRefinementPrompt: FC<StartRefinementProps> = ({
  treatUrl,
  open,
  setOpen,
}) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation("common")
  const childWindowRef = useRef(null)
  let timer = null
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return
      console.log("Received:", event.data)
      setOpen(false)
      window.removeEventListener("message", handleMessage)
      clearInterval(timer)
    }
    if (open && !window.location.href.includes("clinical")) {
      window.addEventListener("message", handleMessage)
    } else {
      window.removeEventListener("message", handleMessage)
    }
    return () => window.removeEventListener("message", handleMessage)
  }, [open])

  // click uassist-refinement button
  const clickUassistRefinement = () => {
    if (!treatUrl) {
      viewControlInTreatment.setViewType("front")
      dispatch(openRefinement({}))
      setOpen(false)
    } else {
      // open window
      const data = { type: "uassistRefinement" }
      childWindowRef.current = window.open(treatUrl, "_blank")
      timer = setInterval(() => {
        if (childWindowRef.current?.closed) {
          clearInterval(timer)
          return
        }
        if (childWindowRef.current) {
          childWindowRef.current.postMessage(data, window.location.origin)
        }
      }, 100)
    }
  }

  // click self-refinement
  const clickSelfRefinement = () => {
    if (!treatUrl) {
      viewControlInTreatment.setViewType("front")
      dispatch(openRefinement({ isSelfplan: true }))
      setOpen(false)
    } else {
      // open window
      const data = { type: "selfRefinement" }
      childWindowRef.current = window.open(treatUrl, "_blank")
      timer = setInterval(() => {
        if (childWindowRef.current?.closed) {
          clearInterval(timer)
          return
        }
        if (childWindowRef.current) {
          childWindowRef.current.postMessage(data, window.location.origin)
        }
      }, 100)
    }
  }
  return (
    <Dialog open={open}>
      <DialogTitle sx={{ padding: "16px 24px" }}>
        <Typography variant="inherit" color={"rgba(0, 0, 0, 0.87)"}>
          {`Start Refinement`}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ marginTop: "20px" }} variant="body1" color={"#000"}>
          {`What type of refinement would you like to start?`}
        </Typography>
        <Box component={"div"} sx={{ display: "flex", marginTop: "16px" }}>
          <Box width={248} sx={{ marginRight: "16px" }}>
            <Box
              onClick={() => {
                clickSelfRefinement()
              }}
              sx={{
                cursor: "pointer",
                width: "100%",
                height: "80px",
                backgroundColor: "#215ECD",
                borderRadius: "8px",
                textAlign: "center",
                padding: "8px",
                lineHeight: "32px",
              }}
            >
              <Typography variant="h6" color="#fff">
                {`Self-Planned`}
              </Typography>
              <Typography variant="h6" color="#fff">
                {`Refinement`}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              color={"rgba(0, 0, 0, 0.60)"}
              marginTop={"16px"}
            >
              <Trans
                components={{
                  br: <br />,
                }}
              >
                {`I will treatment plan the case myself.`}
              </Trans>
            </Typography>
          </Box>
          <Box width={248}>
            <Box
              onClick={() => {
                clickUassistRefinement()
              }}
              sx={{
                cursor: "pointer",
                width: "100%",
                height: "80px",
                backgroundColor: "#215ECD",
                borderRadius: "8px",
                textAlign: "center",
                padding: "8px",
                lineHeight: "80px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" color="#fff">
                {`uAssist Refinement`}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              color={"rgba(0, 0, 0, 0.60)"}
              marginTop={"16px"}
            >
              <Trans
                components={{
                  br: <br />,
                }}
              >
                {`uAssist to treatment plan the case following my instructions.`}
              </Trans>
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <UButton
          onClickHandler={() => {
            setOpen(false)
          }}
          variant={"text"}
          btnText={"CANCEL"}
        />
      </DialogActions>
    </Dialog>
  )
}
export default StartRefinementPrompt
